import { useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Plus, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const DAYS = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu", "Minggu"];
const HOURS = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`);
const timeCls = "h-9 px-2 rounded-lg border border-border bg-card text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/40";

function TimeSelect({ value, onChange, testId }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={timeCls} data-testid={testId}>
      {HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
    </select>
  );
}

export function AvailabilityEditor({ availability, onSaved }) {
  const [rows, setRows] = useState(() => DAYS.map((_, i) => (availability?.[String(i)] || []).map((r) => ({ ...r }))));
  const [saving, setSaving] = useState(false);
  const update = (i, fn) => setRows((rs) => rs.map((r, j) => (j === i ? fn(r) : r)));

  const save = async () => {
    const payload = {};
    rows.forEach((ranges, i) => { if (ranges.length) payload[String(i)] = ranges; });
    setSaving(true);
    try { const { data } = await api.patch("/talent/me", { availability: payload }); toast.success("Jadwal ketersediaan disimpan"); onSaved(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <details className="card-soft p-5 md:col-span-2" data-testid="availability-editor">
      <summary className="cursor-pointer font-heading font-medium text-slate-800 inline-flex items-center gap-2"><CalendarDays size={16} strokeWidth={1.5} className="text-primary" />Jadwal ketersediaan (WIB)</summary>
      <p className="mt-2 text-sm text-slate-500 leading-relaxed">User hanya bisa memesan slot di dalam jam ini dan yang belum terisi. Jam selesai lebih kecil dari jam mulai berarti lewat tengah malam (mis. 21:00 – 01:00).</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {rows.map((ranges, i) => (
          <div key={i} className="rounded-lg border border-border p-3" data-testid={`availability-row-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">{DAYS[i]}</span>
              <button type="button" onClick={() => update(i, (r) => [...r, { start: "19:00", end: "23:00" }])} className="btn-ghost h-8 px-2 text-xs" data-testid={`availability-add-${i}`}><Plus size={14} strokeWidth={1.5} />Tambah jam</button>
            </div>
            {ranges.length === 0 && <p className="text-xs text-slate-400 mt-1" data-testid={`availability-off-${i}`}>Libur</p>}
            {ranges.map((r, k) => (
              <div key={k} className="mt-2 flex items-center gap-2">
                <TimeSelect value={r.start} onChange={(v) => update(i, (rs) => rs.map((x, m) => (m === k ? { ...x, start: v } : x)))} testId={`availability-start-${i}-${k}`} />
                <span className="text-slate-400 text-xs">–</span>
                <TimeSelect value={r.end} onChange={(v) => update(i, (rs) => rs.map((x, m) => (m === k ? { ...x, end: v } : x)))} testId={`availability-end-${i}-${k}`} />
                <button type="button" onClick={() => update(i, (rs) => rs.filter((_, m) => m !== k))} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors" data-testid={`availability-remove-${i}-${k}`} aria-label="Hapus"><X size={14} strokeWidth={1.5} /></button>
              </div>
            ))}
          </div>
        ))}
      </div>
      <button onClick={save} disabled={saving} className="btn-primary h-10 px-5 mt-4" data-testid="availability-save">{saving ? "Menyimpan..." : "Simpan jadwal"}</button>
    </details>
  );
}
